// Ask the system picker for portable images. In particular, iOS can export
// library HEIC photos as JPEG instead of passing an unsupported original.
export const IMAGE_PICKER_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif';
export const IMAGE_PROCESSING_TIMEOUT = 30000;
const MAX_EDGE = 1080;
const MAX_BYTES = 512 * 1024;

const imageError = code => Object.assign(new Error(code), { code });

// Image decoding/encoding callbacks can fail to settle (e.g. an unavailable
// photo). Race each stage with the same deadline and release its resources.
function stage(signal, start) {
  return new Promise((resolve, reject) => {
    let finished = false;
    let cleanup;
    const finish = (error, result) => {
      if (finished) return;
      finished = true;
      signal.removeEventListener('abort', abort);
      cleanup?.();
      if (error) reject(error); else resolve(result);
    };
    const abort = () => finish(signal.reason || imageError('IMAGE_CANCELLED'));
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    try {
      cleanup = start(result => finish(null, result), error => finish(error));
      if (finished) cleanup?.();
    } catch (error) { finish(error); }
  });
}

export async function handleImageUpload(file, { signal: callerSignal } = {}) {
  if (!(file instanceof Blob) || !file.size) throw imageError('IMAGE_UNREADABLE');
  const controller = new AbortController();
  const { signal } = controller;
  let cancellationError;
  const abortWith = code => {
    if (signal.aborted) return;
    cancellationError = imageError(code);
    controller.abort(cancellationError);
  };
  const cancel = () => abortWith('IMAGE_CANCELLED');
  if (callerSignal?.aborted) cancel();
  callerSignal?.addEventListener('abort', cancel, { once: true });
  const timer = setTimeout(() => abortWith('IMAGE_TIMEOUT'), IMAGE_PROCESSING_TIMEOUT);
  let objectURL;
  let image;
  let canvas;
  const canvasLost = () => abortWith('IMAGE_UNREADABLE');
  try {
    if (signal.aborted) throw signal.reason;
    objectURL = URL.createObjectURL(file);
    image = new Image();
    await stage(signal, (resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(imageError('IMAGE_UNREADABLE'));
      image.src = objectURL;
      return () => { image.onload = null; image.onerror = null; };
    });
    if (!image.naturalWidth || !image.naturalHeight) throw imageError('IMAGE_UNREADABLE');
    // A loaded image is not necessarily ready for rasterization. Waiting for
    // decode keeps the expensive decode off the drawImage call where supported
    // and gives failed/stalled decoders the same deadline as the picker flow.
    if (typeof image.decode === 'function') {
      await stage(signal, (resolve, reject) => {
        image.decode().then(resolve, () => reject(imageError('IMAGE_UNREADABLE')));
      });
    }

    // Decode with the browser's orientation handling and draw straight into a
    // small canvas. Do not allocate a full-resolution canvas for 12–48 MP photos.
    canvas = document.createElement('canvas');
    canvas.addEventListener('contextlost', canvasLost);
    const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    let width = Math.max(1, Math.round(image.naturalWidth * scale));
    let height = Math.max(1, Math.round(image.naturalHeight * scale));
    const jpeg = /^image\/(?:jpe?g|hei[cf])$/i.test(file.type)
      || ((!file.type || file.type === 'application/octet-stream') && /\.(?:jpe?g|hei[cf])$/i.test(file.name || ''));
    const type = jpeg ? 'image/jpeg' : 'image/png';
    let quality = 0.9;
    let encoded;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      if (signal.aborted) throw signal.reason;
      canvas.width = width;
      canvas.height = height;
      // This canvas exists only to encode pixels, never to animate on screen.
      // Prefer CPU/readback storage so Android does not depend on a large GPU
      // texture surviving a gallery/app transition. Normalize wide-gamut photos
      // to the same portable sRGB output as the rest of the editor.
      const context = canvas.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' });
      const assertContext = () => {
        if (signal.aborted) throw signal.reason;
        if (!context || context.isContextLost?.()) throw imageError('IMAGE_UNREADABLE');
      };
      assertContext();
      context.drawImage(image, 0, 0, width, height);
      assertContext();
      const encodingQuality = quality;
      encoded = await stage(signal, (resolve, reject) => {
        canvas.toBlob(blob => blob?.size ? resolve(blob) : reject(imageError('IMAGE_UNREADABLE')), type, encodingQuality);
      });
      // Context loss can otherwise encode a valid but empty/black JPEG. A
      // non-null Blob alone is not proof that rasterization succeeded.
      assertContext();
      if (encoded.size <= MAX_BYTES) break;
      // Keep screenshot transparency. Native PNG encoding is asynchronous and
      // avoids the old JS quantizer blocking the UI for several iterations.
      if (jpeg && quality > 0.65) quality -= 0.12;
      else { width = Math.max(1, Math.floor(width * 0.8)); height = Math.max(1, Math.floor(height * 0.8)); }
    }
    if (encoded.size > MAX_BYTES) throw imageError('IMAGE_UNREADABLE');
    const link = await stage(signal, (resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reader.onabort = () => reject(imageError('IMAGE_UNREADABLE'));
      reader.readAsDataURL(encoded);
      return () => {
        reader.onload = reader.onerror = reader.onabort = null;
        if (reader.readyState === FileReader.LOADING) reader.abort();
      };
    });
    if (signal.aborted) throw signal.reason;
    if (!/^data:image\/(?:jpeg|png);base64,\S+$/.test(link)) throw imageError('IMAGE_UNREADABLE');
    return { data: { link } };
  } catch (error) {
    // Older WebViews support AbortController but not AbortSignal.reason.
    throw cancellationError || error;
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', cancel);
    if (image) image.src = '';
    if (objectURL) URL.revokeObjectURL(objectURL);
    if (canvas) {
      canvas.removeEventListener('contextlost', canvasLost);
      canvas.width = 0; canvas.height = 0;
    }
  }
}
